import { Home, Plus, BarChart3, Users, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

interface BottomNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onNewDecision: () => void;
}

const navItems = [
  { id: 'home', label: 'Start', icon: Home },
  { id: 'insights', label: 'Analysen', icon: BarChart3 },
  { id: 'new', label: 'Neu', icon: Plus, isAction: true },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'settings', label: 'Einstellungen', icon: Settings },
];

// Haptic feedback utility
const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if ('vibrate' in navigator) {
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 30,
    };
    navigator.vibrate(patterns[type]);
  }
};

export function BottomNavigation({ activeTab, onTabChange, onNewDecision }: BottomNavigationProps) {
  const navigate = useNavigate();

  const handleItemClick = useCallback((item: typeof navItems[0]) => {
    // Trigger haptic feedback
    triggerHaptic(item.isAction ? 'medium' : 'light');
    
    if (item.id === 'new') {
      onNewDecision();
    } else if (item.id === 'settings') {
      navigate('/settings/workspace');
    } else {
      onTabChange(item.id);
    }
  }, [navigate, onNewDecision, onTabChange]);

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-md border-t border-border/50 safe-area-bottom"
      role="navigation"
      aria-label="Mobile Navigation"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;

          if (item.isAction) {
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="flex flex-col items-center justify-center -mt-4"
                aria-label="Neue Entscheidung erstellen"
              >
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/25 transition-transform active:scale-95">
                  <Icon className="w-5 h-5 text-primary-foreground" />
                </div>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-3 py-2 rounded-lg transition-colors min-w-[60px]",
                isActive 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
            >
              <Icon className={cn(
                "w-5 h-5 transition-transform",
                isActive && "scale-110"
              )} />
              <span className={cn(
                "text-[10px] font-medium transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )}>
                {item.label}
              </span>
              {isActive && (
                <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
